import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  Rss,
  MoreVertical,
  Edit2,
  Trash2,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { AdminClientApi, ClientFeed, GetFeedsInputInterface } from '../../Admin-ClientLibrary';
import './Feeds.css';
import { getApiUrl } from '../../config/api';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const Feeds: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [feeds, setFeeds] = useState<ClientFeed[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || '');
  const [selectedFeeds, setSelectedFeeds] = useState<Set<number>>(new Set());
  const [totalFeeds, setTotalFeeds] = useState<number>(0);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Page history stack for cursor-based pagination
  const [pageHistory, setPageHistory] = useState<
    Array<{
      cursor: {
        beforeId?: number;
        afterId?: number;
        beforeCreatedDate?: string;
        afterCreatedDate?: string;
      } | null;
      pageNum: number;
    }>
  >([{ cursor: null, pageNum: 1 }]);

  // Pagination and ordering state
  const [orderBy, setOrderBy] = useState<string>(searchParams.get('orderBy') || 'id');
  const [orderDirection, setOrderDirection] = useState<string>(
    searchParams.get('orderDirection') || 'DESC'
  );

  // Initialize filters from URL parameters
  const [filters, setFilters] = useState({
    createdAfter: searchParams.get('createdAfter') || '',
    createdBefore: searchParams.get('createdBefore') || '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    createdAfter: searchParams.get('createdAfter') || '',
    createdBefore: searchParams.get('createdBefore') || '',
  });

  const limit = 20;

  // Function to update URL parameters
  const updateUrlParams = (params: Record<string, string | number | undefined>): void => {
    const newParams = new URLSearchParams();

    // Add all current search params first
    searchParams.forEach((value, key) => {
      newParams.set(key, value);
    });

    // Update with new params
    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== '') {
        newParams.set(key, value.toString());
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  };

  // Fetch feeds with pagination and filtering
  const fetchFeeds = async (
    direction: 'first' | 'next' | 'prev',
    cursor?: {
      beforeId?: number;
      afterId?: number;
      beforeCreatedDate?: string;
      afterCreatedDate?: string;
    },
    search?: string,
    filterParams?: { createdAfter: string; createdBefore: string },
    orderByParam?: string,
    orderDirectionParam?: string,
    getCount: boolean = false
  ): Promise<void> => {
    setLoading(true);
    setError('');

    try {
      const apiParams: GetFeedsInputInterface = {
        limit: 20,
        getCount,
      };

      // Add search if provided
      if (search) {
        apiParams.search = search;
      }

      // Add order parameters
      if (orderByParam) {
        apiParams.orderBy = orderByParam;
      }
      if (orderDirectionParam) {
        apiParams.orderDirection = orderDirectionParam;
      }

      // Add cursor parameters if provided
      if (cursor) {
        if (cursor.beforeId) apiParams.beforeId = cursor.beforeId;
        if (cursor.afterId) apiParams.afterId = cursor.afterId;
        if (cursor.beforeCreatedDate) apiParams.beforeCreatedDate = cursor.beforeCreatedDate;
        if (cursor.afterCreatedDate) apiParams.afterCreatedDate = cursor.afterCreatedDate;
      }

      // Add filter parameters if provided
      if (filterParams) {
        // Only add filter parameters if they have values
        if (filterParams.createdAfter) {
          apiParams.createdAfter = filterParams.createdAfter;
        }
        if (filterParams.createdBefore) {
          apiParams.createdBefore = filterParams.createdBefore;
        }
      }

      console.log('Fetching feeds with params:', apiParams);

      const response = await adminApi.getFeeds(apiParams);

      if (response.success) {
        setFeeds(response.feeds);
        if (getCount) {
          setTotalFeeds(response.total);
        }
      } else {
        setError(response.message || 'Failed to fetch feeds');
        setFeeds([]);
      }
    } catch (err: any) {
      console.error('Error fetching feeds:', err);
      setError('Network error occurred');
      setFeeds([]);
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    console.log('Feeds component mounted, fetching data...');
    fetchFeeds('first', undefined, searchTerm, appliedFilters, orderBy, orderDirection, true);
  }, []);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    updateUrlParams({ search: searchTerm });
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);
    fetchFeeds('first', undefined, searchTerm, appliedFilters, orderBy, orderDirection, true);
  };

  const handleSelectFeed = (feedId: number): void => {
    const newSelectedFeeds = new Set(selectedFeeds);
    if (newSelectedFeeds.has(feedId)) {
      newSelectedFeeds.delete(feedId);
    } else {
      newSelectedFeeds.add(feedId);
    }
    setSelectedFeeds(newSelectedFeeds);
  };

  const handleSelectAll = (): void => {
    if (selectedFeeds.size === feeds.length && feeds.length > 0) {
      setSelectedFeeds(new Set());
    } else {
      setSelectedFeeds(new Set(feeds.map((feed) => feed.content_id)));
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString();
  };

  const getCursorForFeed = (feed: ClientFeed) => {
    return {
      beforeId: feed.content_id,
      afterId: feed.content_id,
      beforeCreatedDate: feed.createdAt.toISOString(),
      afterCreatedDate: feed.createdAt.toISOString(),
    };
  };

  const handleNextPage = (): void => {
    if (feeds.length === 0) return;

    const lastFeed = feeds[feeds.length - 1];
    const cursor = getCursorForFeed(lastFeed);

    // Adjust cursor based on order direction
    const nextCursor =
      orderBy === 'id'
        ? orderDirection === 'DESC'
          ? { beforeId: cursor.beforeId }
          : { afterId: cursor.afterId }
        : orderDirection === 'DESC'
          ? { beforeCreatedDate: cursor.beforeCreatedDate }
          : { afterCreatedDate: cursor.afterCreatedDate };

    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    setPageHistory([...pageHistory, { cursor: nextCursor, pageNum: newPage }]);
    fetchFeeds('next', nextCursor, searchTerm, appliedFilters, orderBy, orderDirection, false);

    updateUrlParams({
      page: newPage,
      ...nextCursor,
    });
  };

  const handlePrevPage = (): void => {
    if (currentPage <= 1 || pageHistory.length <= 1) return;

    // Remove current page from history
    const newHistory = pageHistory.slice(0, -1);
    const prevPageData = newHistory[newHistory.length - 1];

    setCurrentPage(prevPageData.pageNum);
    setPageHistory(newHistory);
    fetchFeeds(
      'prev',
      prevPageData.cursor || undefined,
      searchTerm,
      appliedFilters,
      orderBy,
      orderDirection,
      false
    );

    updateUrlParams({
      page: prevPageData.pageNum,
      ...(prevPageData.cursor || {}),
    });
  };

  const handleFirstPage = (): void => {
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);
    fetchFeeds('first', undefined, searchTerm, appliedFilters, orderBy, orderDirection, true);
    updateUrlParams({ page: 1 });
  };

  const handleOrderByChange = (newOrderBy: string): void => {
    setOrderBy(newOrderBy);
    updateUrlParams({ orderBy: newOrderBy });
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);
    fetchFeeds('first', undefined, searchTerm, appliedFilters, newOrderBy, orderDirection, true);
  };

  const handleOrderDirectionChange = (newDirection: string): void => {
    setOrderDirection(newDirection);
    updateUrlParams({ orderDirection: newDirection });
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);
    fetchFeeds('first', undefined, searchTerm, appliedFilters, orderBy, newDirection, true);
  };

  const handleFilterChange = (filterName: string, value: string): void => {
    setFilters({ ...filters, [filterName]: value });
  };

  const applyFilters = (): void => {
    setAppliedFilters(filters);
    setShowFilters(false);
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);

    // Update URL with filter parameters
    updateUrlParams({
      createdAfter: filters.createdAfter,
      createdBefore: filters.createdBefore,
    });

    fetchFeeds('first', undefined, searchTerm, filters, orderBy, orderDirection, true);
  };

  const clearFilters = (): void => {
    const clearedFilters = {
      createdAfter: '',
      createdBefore: '',
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setShowFilters(false);
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);

    // Clear filter parameters from URL
    updateUrlParams({
      createdAfter: undefined,
      createdBefore: undefined,
    });

    fetchFeeds('first', undefined, searchTerm, clearedFilters, orderBy, orderDirection, true);
  };

  const getFilterSummary = (): string => {
    const activeParts = [];

    if (appliedFilters.createdAfter) {
      activeParts.push(`After ${new Date(appliedFilters.createdAfter).toLocaleDateString()}`);
    }
    if (appliedFilters.createdBefore) {
      activeParts.push(`Before ${new Date(appliedFilters.createdBefore).toLocaleDateString()}`);
    }
    if (searchTerm) {
      activeParts.push(`Search: "${searchTerm}"`);
    }

    if (activeParts.length === 0) {
      return `All feeds | Ordered by ${
        orderBy === 'id' ? 'ID' : 'Created Date'
      } (${orderDirection}). Total: ${totalFeeds}`;
    }

    return `${activeParts.join(', ')} | Ordered by ${
      orderBy === 'id' ? 'ID' : 'Created Date'
    } (${orderDirection}). Total: ${totalFeeds}`;
  };

  const handleRowClick = (feedId: number, event: React.MouseEvent): void => {
    // Prevent navigation if clicking on checkbox, button, or action elements
    const target = event.target as HTMLElement;
    if (
      (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') ||
      target.tagName === 'BUTTON' ||
      target.closest('button') ||
      target.closest('.actions')
    ) {
      return;
    }
    // Navigate to feed detail page (when implemented)
    navigate(`/feeds/${feedId}`);
  };

  return (
    <div className="feeds-page">
      <div className="feeds-header">
        <div className="header-top">
          <h1>Feeds</h1>
          <div className="header-stats">
            <span className="stat-item">
              <Rss size={16} />
              {totalFeeds} Total Feeds
            </span>
          </div>
        </div>

        {/* Search and Controls Row */}
        <div className="search-and-controls">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search feeds..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-secondary">
              Search
            </button>
          </form>

          <div className="controls-row">
            {/* Order By Controls */}
            <div className="order-controls">
              <div className="order-group">
                <label>Order by:</label>
                <select
                  value={orderBy}
                  onChange={(e) => handleOrderByChange(e.target.value)}
                  className="order-select"
                >
                  <option value="id">ID</option>
                  <option value="createdAt">Created Date</option>
                </select>
              </div>
              <div className="order-group">
                <label>Direction:</label>
                <select
                  value={orderDirection}
                  onChange={(e) => handleOrderDirectionChange(e.target.value)}
                  className="order-select"
                >
                  <option value="DESC">
                    <ChevronDown size={16} /> Descending
                  </option>
                  <option value="ASC">
                    <ChevronUp size={16} /> Ascending
                  </option>
                </select>
              </div>
            </div>

            <button className="btn-secondary" onClick={() => setShowFilters(!showFilters)}>
              <Filter size={16} />
              Filter{' '}
              {Object.values(appliedFilters).some((f) => f) && (
                <span className="filter-badge">•</span>
              )}
            </button>
          </div>
        </div>

        <div className="filter-summary">{getFilterSummary()}</div>

        {showFilters && (
          <div className="filters-dropdown">
            <div className="filters-header">
              <h3>Filters</h3>
              <button className="btn-text" onClick={clearFilters}>
                Clear All
              </button>
            </div>

            <div className="filter-group">
              <label>Created After:</label>
              <input
                type="date"
                placeholder="Enter Created After"
                value={filters.createdAfter}
                onChange={(e) => handleFilterChange('createdAfter', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Created Before:</label>
              <input
                type="date"
                placeholder="Enter Created Before"
                value={filters.createdBefore}
                onChange={(e) => handleFilterChange('createdBefore', e.target.value)}
              />
            </div>

            <div className="filters-actions">
              <button className="btn-secondary" onClick={() => setShowFilters(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={applyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {selectedFeeds.size > 0 && (
          <div className="bulk-actions">
            <span>{selectedFeeds.size} selected</span>
            <button className="btn-secondary">
              <Edit2 size={14} />
              Edit
            </button>
            <button className="btn-danger">
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        )}
      </div>

      <div className="feeds-content">
        {error && (
          <div className="error-message">
            <p>Error: {error}</p>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading feeds...</div>
        ) : (
          <>
            <div className="feeds-table-container">
              <table className="feeds-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedFeeds.size === feeds.length && feeds.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Content ID</th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {feeds.map((feed) => (
                    <tr
                      key={feed.content_id}
                      onClick={(e) => handleRowClick(feed.content_id, e)}
                      className="feed-row"
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedFeeds.has(feed.content_id)}
                          onChange={() => handleSelectFeed(feed.content_id)}
                        />
                      </td>
                      <td>
                        <button
                          className="clickable-id-button"
                          onClick={() => navigate(`/feeds/${feed.content_id}`)}
                        >
                          {feed.content_id}
                        </button>
                      </td>
                      <td>
                        <div className="feed-title">{feed.title}</div>
                      </td>
                      <td>
                        <div className="feed-description">
                          {feed.description || (
                            <span className="no-description">No description</span>
                          )}
                        </div>
                      </td>
                      <td>{formatDate(feed.createdAt)}</td>
                      <td>{formatDate(feed.updatedAt)}</td>
                      <td>
                        <div className="actions">
                          <button className="action-btn" title="View Details">
                            <Eye size={14} />
                          </button>
                          <button className="action-btn" title="Edit">
                            <Edit2 size={14} />
                          </button>
                          <button className="action-btn" title="More Options">
                            <MoreVertical size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="pagination-info">Showing {feeds.length} feeds</div>
              <div className="pagination-controls">
                <button
                  onClick={handleFirstPage}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  First
                </button>
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <span className="page-info">Page {currentPage}</span>
                <button onClick={handleNextPage} className="btn-secondary">
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Feeds;
